import { useIsMutating } from '@tanstack/react-query';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { CompleteOrderError } from 'typescript/constants/enums/checkout';
import { MUTATION_KEYS } from 'typescript/constants/react_query';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';

interface ICompleteOrderContext {
  isCompletingOrder: boolean;
  showRecaptcha: boolean;
  setShowRecaptcha: Dispatch<SetStateAction<boolean>>;
  completeOrderError: CompleteOrderError | null;
  setCompleteOrderError: Dispatch<SetStateAction<CompleteOrderError | null>>;
  showCompletingOrderDialog: boolean;
  setShowCompletingOrderDialog: Dispatch<SetStateAction<boolean>>;
  showAlternateCompletingCopy: boolean;
  setShowAlternateCompletingCopy: Dispatch<SetStateAction<boolean>>;
}

const CompleteOrderContext = createContext<ICompleteOrderContext | undefined>(
  undefined,
);

const useCompleteOrderContext = () => {
  const context = useContext(CompleteOrderContext);
  if (context === undefined) {
    throw new Error(
      'useCompleteOrderContext must be used within a CompleteOrderProvider',
    );
  }
  return context;
};

interface CompleteOrderProviderProps {
  children: ReactNode;
}

const CompleteOrderProvider = ({ children }: CompleteOrderProviderProps) => {
  const { orderNumber } = useCheckoutFlow();
  const isCompletingOrder =
    useIsMutating({
      mutationKey: [MUTATION_KEYS.ORDER_COMPLETE, { orderNumber }],
    }) > 0;

  const [completeOrderError, setCompleteOrderError] =
    useState<CompleteOrderError>(null);

  const [showRecaptcha, setShowRecaptcha] = useState(false);

  const [showCompletingOrderDialog, setShowCompletingOrderDialog] =
    useState(false);
  const [showAlternateCompletingCopy, setShowAlternateCompletingCopy] =
    useState(false);

  return (
    <CompleteOrderContext.Provider
      value={{
        isCompletingOrder,
        showRecaptcha,
        setShowRecaptcha,
        completeOrderError,
        setCompleteOrderError,
        showCompletingOrderDialog,
        setShowCompletingOrderDialog,
        showAlternateCompletingCopy,
        setShowAlternateCompletingCopy,
      }}
    >
      {children}
    </CompleteOrderContext.Provider>
  );
};

export { CompleteOrderProvider, useCompleteOrderContext };
